import { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Send, Instagram } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Logo } from '@/components/Logo';
import { toast } from 'sonner';

interface ContactPageProps {
  onBack: () => void;
}

export function ContactPage({ onBack }: ContactPageProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || !message.trim()) {
      toast.error('Add your name, a valid email and a message.'); return;
    }
    window.location.href = `mailto:appsportara@gmail.com?subject=${encodeURIComponent(`Sportara help from ${name.trim()}`)}&body=${encodeURIComponent(`${message.trim()}\n\nReply to: ${email.trim()}`)}`;
    toast.info('Your email app will open with a draft. Send it there to contact us.');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Nav */}
      <nav className="bg-secondary text-secondary-foreground">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={onBack} className="flex items-center gap-2 text-sm font-display font-bold text-secondary-foreground/70 hover:text-secondary-foreground transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <div className="flex items-center gap-2">
            <Logo className="w-6 h-6" alt="Sportara" />
            <span className="font-display font-bold text-sm">Sportara</span>
          </div>
        </div>
      </nav>

      <div className="max-w-lg mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="text-center mb-10"
        >
          <h1 className="font-display font-bold text-3xl sm:text-4xl mb-2">Contact Us</h1>
          <p className="text-muted-foreground font-body">
            Have a question, suggestion, or need help? We'd love to hear from you.
          </p>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.4 }}
          onSubmit={handleSubmit}
          className="bg-card rounded-2xl border border-border p-6 shadow-card space-y-5"
        >
          <div className="space-y-2">
            <Label htmlFor="name" className="font-display font-bold text-sm">Name</Label>
            <Input
              id="name"
              placeholder="Your name"
              value={name}
              onChange={e => setName(e.target.value)}
              maxLength={100}
              className="rounded-xl"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email" className="font-display font-bold text-sm">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              maxLength={255}
              className="rounded-xl"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="message" className="font-display font-bold text-sm">Message</Label>
            <Textarea
              id="message"
              placeholder="How can we help?"
              value={message}
              onChange={e => setMessage(e.target.value)}
              maxLength={1000}
              rows={5}
              className="rounded-xl"
            />
          </div>
          <Button
            type="submit"
            
            className="w-full h-12 font-display font-bold bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl"
          >
            Open email draft <Send className="ml-2 w-4 h-4" />
          </Button>
        </motion.form>

        {/* Social */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-center mt-8"
        >
          <p className="text-sm text-muted-foreground font-body mb-3">Or reach out directly</p>
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <a
              href="mailto:appsportara@gmail.com"
              className="inline-flex items-center gap-2 bg-card border border-border rounded-xl px-4 py-2.5 hover:border-primary/30 transition-colors"
            >
              <Send className="w-4 h-4 text-primary" />
              <span className="font-display font-bold text-sm">appsportara@gmail.com</span>
            </a>
            <a
              href="https://instagram.com/sportaraapp"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-card border border-border rounded-xl px-4 py-2.5 hover:border-primary/30 transition-colors"
            >
              <Instagram className="w-5 h-5 text-primary" />
              <span className="font-display font-bold text-sm">@sportaraapp</span>
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
